/**
 * Flames Army — Wishlist
 * Paso 1: core de datos.
 *
 * Este archivo NO pinta nada en pantalla. Solo se encarga de guardar,
 * leer y borrar productos, y de avisarle al resto del tema cuando la
 * lista cambia (evento `wishlist:change`).
 *
 * Todo el acceso al almacenamiento pasa por acá. Si algún día movemos
 * la wishlist a metafields de cliente, solo cambian las funciones
 * `read()` y `persist()` y el resto del tema ni se entera.
 *
 * API pública (window.Wishlist):
 *   .get()                    -> array de items
 *   .has(productId)           -> true / false
 *   .add({...})               -> agrega (o actualiza) y devuelve el array
 *   .remove(productId)        -> quita y devuelve el array
 *   .toggle({...})            -> agrega si no está, quita si está
 *   .count()                  -> número de items
 *   .merge([...])             -> fusiona una lista externa (URL compartida)
 *   .clear()                  -> vacía la lista
 *   .ids()                    -> array de ids como strings
 *   .EVENT                    -> 'wishlist:change'
 */

window.Wishlist = (function () {
  'use strict';

  var STORAGE_KEY = 'flames_wishlist_v1';
  var EVENT_NAME = 'wishlist:change';
  var MAX_ITEMS = 100; // tope de seguridad, evita que crezca sin control

  // Si localStorage falla (Safari privado, bloqueadores), guardamos en memoria.
  var memoryStore = [];
  var usingMemory = false;

  /* ------------------------------------------------------------------
   * Capa de almacenamiento — lo único que cambiaría en el nivel 3
   * ------------------------------------------------------------------ */

  function read() {
    if (usingMemory) return memoryStore.slice();

    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter(isValidItem) : [];
    } catch (e) {
      // JSON corrupto o localStorage bloqueado: arrancamos limpio en memoria.
      usingMemory = true;
      return memoryStore.slice();
    }
  }

  function persist(items) {
    memoryStore = items.slice();

    if (usingMemory) return true;

    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      return true;
    } catch (e) {
      // Cuota llena o acceso denegado: seguimos en memoria sin romper nada.
      usingMemory = true;
      return false;
    }
  }

  /* ------------------------------------------------------------------
   * Helpers
   * ------------------------------------------------------------------ */

  // Los IDs de Shopify son números grandes. Los normalizamos a string
  // siempre, para que comparar nunca dependa del tipo.
  function normalizeId(value) {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  }

  function isValidItem(item) {
    return item && typeof item === 'object' && normalizeId(item.id) !== '';
  }

  function buildItem(input) {
    return {
      id: normalizeId(input.id),
      variantId: normalizeId(input.variantId),
      handle: input.handle ? String(input.handle) : '',
      addedAt: input.addedAt || new Date().toISOString()
    };
  }

  function indexOfId(items, productId) {
    var id = normalizeId(productId);
    for (var i = 0; i < items.length; i++) {
      if (normalizeId(items[i].id) === id) return i;
    }
    return -1;
  }

  // Un solo punto de salida: cada cambio persiste y avisa.
  function commit(items, action, item) {
    var trimmed = items.slice(0, MAX_ITEMS);
    persist(trimmed);
    announce(trimmed, action, item);
    return trimmed;
  }

  function announce(items, action, item) {
    var detail = {
      items: items,
      count: items.length,
      action: action || 'update',
      item: item || null,
      persistent: !usingMemory
    };

    try {
      document.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: detail }));
    } catch (e) {
      // Fallback para navegadores viejos sin constructor de CustomEvent.
      var evt = document.createEvent('CustomEvent');
      evt.initCustomEvent(EVENT_NAME, true, true, detail);
      document.dispatchEvent(evt);
    }
  }

  /* ------------------------------------------------------------------
   * API pública
   * ------------------------------------------------------------------ */

  function get() {
    return read();
  }

  function ids() {
    return read().map(function (item) {
      return normalizeId(item.id);
    });
  }

  function has(productId) {
    return indexOfId(read(), productId) !== -1;
  }

  function count() {
    return read().length;
  }

  function add(input) {
    if (!input || normalizeId(input.id) === '') return read();

    var items = read();
    var item = buildItem(input);
    var existing = indexOfId(items, item.id);

    if (existing !== -1) {
      // Ya estaba: conservamos la fecha original y actualizamos la variante.
      item.addedAt = items[existing].addedAt || item.addedAt;
      items.splice(existing, 1);
    }

    // Lo más reciente arriba.
    items.unshift(item);
    return commit(items, 'add', item);
  }

  function remove(productId) {
    var items = read();
    var position = indexOfId(items, productId);
    if (position === -1) return items;

    var removed = items.splice(position, 1)[0];
    return commit(items, 'remove', removed);
  }

  function toggle(input) {
    if (!input || normalizeId(input.id) === '') return read();
    return has(input.id) ? remove(input.id) : add(input);
  }

  // Para el paso 5 (compartir por URL) y para el día que sincronicemos
  // con la cuenta del cliente: suma lo que falte sin borrar lo que hay.
  function merge(incoming) {
    if (!Array.isArray(incoming)) return read();

    var items = read();
    var added = 0;

    incoming.forEach(function (raw) {
      if (!raw || normalizeId(raw.id) === '') return;
      if (indexOfId(items, raw.id) !== -1) return;
      items.push(buildItem(raw));
      added++;
    });

    if (added === 0) return items;
    return commit(items, 'merge', null);
  }

  function clear() {
    return commit([], 'clear', null);
  }

  /* ------------------------------------------------------------------
   * Sincronización entre pestañas
   * ------------------------------------------------------------------ */

  window.addEventListener('storage', function (event) {
    if (event.key !== STORAGE_KEY) return;
    var items = read();
    announce(items, 'sync', null);
  });

  return {
    EVENT: EVENT_NAME,
    STORAGE_KEY: STORAGE_KEY,
    get: get,
    ids: ids,
    has: has,
    count: count,
    add: add,
    remove: remove,
    toggle: toggle,
    merge: merge,
    clear: clear
  };
})();